import { useEffect, useState } from 'react'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { Label } from '../ui/label'
import { Button } from '../ui/button'
import { Globe, Check, ChevronsUpDown } from 'lucide-react'
import { cn } from '../lib/utils'
import { ContextSearchResult } from 'store/types/searchResults'

interface CountrySelectorProps {
  countries: ContextSearchResult[]
  value?: string
  onChange: (country: ContextSearchResult) => void
  defaultCountryCode?: string // e.g. "oecd1"
}

export const CountrySelector = ({
  countries,
  value,
  onChange,
  defaultCountryCode,
}: CountrySelectorProps) => {
  const [open, setOpen] = useState(false)
  const [selectedSid, setSelectedSid] = useState<string | undefined>(value)

  // 🔹 Auto-select default country (by shortName)
  useEffect(() => {
    if (!countries?.length || !defaultCountryCode || selectedSid) return

    const match = countries?.find(
      (c: ContextSearchResult) =>
        c.fullContext?.shortName?.toLowerCase() ===
        defaultCountryCode.toLowerCase()
    )

    if (match) {
      setSelectedSid(match.sid)
      onChange(match)
    }
  }, [countries, defaultCountryCode, selectedSid, onChange])

  // 🔹 Sync prop `value`
  useEffect(() => {
    if (value) setSelectedSid(value)
  }, [value])

  // 🔹 Handle manual selection
  const handleSelect = (sid: string) => {
    setSelectedSid(sid)
    const country = countries?.find((c: ContextSearchResult) => c.sid === sid)
    if (country) onChange(country)
    setOpen(false)
  }

  const selectedCountry = countries?.find(
    (c: ContextSearchResult) => c.sid === selectedSid
  )

  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-2 text-sm font-medium">
        <Globe className="h-4 w-4 text-chart-1" />
        Country / Region
      </Label>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
          >
            {selectedCountry ? selectedCountry.name : 'Select a Country'}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-full p-0" align="start">
          <Command>
            <CommandInput placeholder="Search country..." />
            <CommandList>
              <CommandEmpty>No country found.</CommandEmpty>
              <CommandGroup>
                {countries?.map((country: ContextSearchResult) => (
                  <CommandItem
                    key={country.sid}
                    value={country.name}
                    onSelect={() => handleSelect(country.sid)}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        selectedSid === country.sid
                          ? 'opacity-100'
                          : 'opacity-0'
                      )}
                    />
                    {country.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
